import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { isAdmin } from './utils/permissions';
import { Settings as SettingsIcon, Bell, Globe, Shield, Clock, Save, Check, DollarSign, Building2, Users, Plus, Trash2 } from 'lucide-react';
import { DEFAULT_PAY_RULES, SECTOR_PRESETS, PAY_TYPES } from './utils/shiftTypes';
import { DEFAULT_ROLE_HIERARCHY, DEFAULT_SUBSTITUTION_RULES } from './utils/roleHierarchy';
import ShiftTemplateManager from './components/ShiftTemplateManager';

const toSchedulingConfig = (settings) => ({
  weekStartDay: settings.weekStartDay,
  minRestHoursBetweenShifts: settings.minRestHours,
  maxWeeklyHours: settings.maxWeeklyHours,
  maxDailyHours: settings.maxDailyHours,
  maxConsecutiveDays: settings.maxConsecutiveDays,
  defaultShiftDuration: settings.defaultShiftDuration,
  autoApproveSwaps: settings.autoApproveSwaps,
  requireSwapApproval: settings.requireSwapApproval,
  notifyOnPublish: settings.notifyOnPublish,
  notifyOnSwapRequest: settings.notifyOnSwapRequest,
  notifyOnShiftChange: settings.notifyOnShiftChange,
  notifyBeforeShift: settings.notifyBeforeShift,
  enableAISuggestions: settings.enableAISuggestions,
  enableAutoAssign: settings.enableAutoAssign,
  showCoverageWarnings: settings.showCoverageWarnings,
});

export default function Settings({ user, userData, companyId }) {
  const { t } = useTranslation();
  // eslint-disable-next-line no-unused-vars
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const [settings, setSettings] = useState({
    companyName: '',
    timezone: 'Europe/London',
    weekStartDay: 1,
    defaultShiftDuration: 8,
    minRestHours: 11,
    maxWeeklyHours: 48,
    maxDailyHours: 13,
    maxConsecutiveDays: 6,
    autoApproveSwaps: false,
    requireSwapApproval: true,
    notifyOnPublish: true,
    notifyOnSwapRequest: true,
    notifyOnShiftChange: true,
    notifyBeforeShift: 60,
    enableAISuggestions: true,
    enableAutoAssign: false,
    showCoverageWarnings: true,
  });

  const [payRules, setPayRules] = useState({ ...DEFAULT_PAY_RULES });
  const [sectorConfig, setSectorConfig] = useState({
    sector: 'general',
    customOverrides: {},
  });
  const [roleHierarchy, setRoleHierarchy] = useState({
    roles: { ...DEFAULT_ROLE_HIERARCHY },
    substitutionRules: { ...DEFAULT_SUBSTITUTION_RULES },
    customRoles: [],
  });
  const [newCustomRole, setNewCustomRole] = useState({ id: '', label: '', level: 1, canSupervise: false });

  useEffect(() => {
    if (!companyId) return;
    const loadSettings = async () => {
      try {
        const compDoc = await getDoc(doc(db, 'companies', companyId));
        if (compDoc.exists()) {
          const data = compDoc.data();
          setCompanySettings(data);
          setSettings(prev => ({
            ...prev,
            companyName: data.name || data.companyName || '',
            timezone: data.shiftsSettings?.timezone || 'Europe/London',
            weekStartDay: data.shiftsSettings?.weekStartDay ?? 1,
            defaultShiftDuration: data.shiftsSettings?.defaultShiftDuration ?? 8,
            minRestHours: data.shiftsSettings?.minRestHours ?? 11,
            maxWeeklyHours: data.shiftsSettings?.maxWeeklyHours ?? 48,
            maxDailyHours: data.shiftsSettings?.maxDailyHours ?? 13,
            maxConsecutiveDays: data.shiftsSettings?.maxConsecutiveDays ?? 6,
            autoApproveSwaps: data.shiftsSettings?.autoApproveSwaps ?? false,
            requireSwapApproval: data.shiftsSettings?.requireSwapApproval ?? true,
            notifyOnPublish: data.shiftsSettings?.notifyOnPublish ?? true,
            notifyOnSwapRequest: data.shiftsSettings?.notifyOnSwapRequest ?? true,
            notifyOnShiftChange: data.shiftsSettings?.notifyOnShiftChange ?? true,
            notifyBeforeShift: data.shiftsSettings?.notifyBeforeShift ?? 60,
            enableAISuggestions: data.shiftsSettings?.enableAISuggestions ?? true,
            enableAutoAssign: data.shiftsSettings?.enableAutoAssign ?? false,
            showCoverageWarnings: data.shiftsSettings?.showCoverageWarnings ?? true,
          }));
          // Load pay rules
          if (data.shiftsSettings?.payRules) {
            setPayRules(prev => ({ ...prev, ...data.shiftsSettings.payRules }));
          }
          // Load sector config
          if (data.shiftsSettings?.sectorConfig) {
            setSectorConfig(prev => ({ ...prev, ...data.shiftsSettings.sectorConfig }));
          }
          // Load role hierarchy
          if (data.shiftsSettings?.roleHierarchy) {
            setRoleHierarchy(prev => ({
              ...prev,
              ...data.shiftsSettings.roleHierarchy,
              roles: { ...DEFAULT_ROLE_HIERARCHY, ...(data.shiftsSettings.roleHierarchy.roles || {}) },
              substitutionRules: { ...DEFAULT_SUBSTITUTION_RULES, ...(data.shiftsSettings.roleHierarchy.substitutionRules || {}) },
            }));
          }
        }
      } catch (err) {
        console.error('Error loading settings:', err);
      }
      setLoading(false);
    };
    loadSettings();
  }, [companyId]);

  const handleSave = async () => {
    if (!companyId) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'companies', companyId), {
        shiftsSettings: {
          timezone: settings.timezone,
          weekStartDay: settings.weekStartDay,
          defaultShiftDuration: settings.defaultShiftDuration,
          minRestHours: settings.minRestHours,
          maxWeeklyHours: settings.maxWeeklyHours,
          maxDailyHours: settings.maxDailyHours,
          maxConsecutiveDays: settings.maxConsecutiveDays,
          autoApproveSwaps: settings.autoApproveSwaps,
          requireSwapApproval: settings.requireSwapApproval,
          notifyOnPublish: settings.notifyOnPublish,
          notifyOnSwapRequest: settings.notifyOnSwapRequest,
          notifyOnShiftChange: settings.notifyOnShiftChange,
          notifyBeforeShift: settings.notifyBeforeShift,
          enableAISuggestions: settings.enableAISuggestions,
          enableAutoAssign: settings.enableAutoAssign,
          showCoverageWarnings: settings.showCoverageWarnings,
          scheduling: toSchedulingConfig(settings),
          payRules,
          sectorConfig,
          roleHierarchy,
        },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Error saving settings:', err);
    }
    setSaving(false);
  };

  const tabs = [
    { id: 'general', label: t('settings.tabs.general'), icon: SettingsIcon },
    { id: 'scheduling', label: t('settings.tabs.scheduling'), icon: Clock },
    { id: 'notifications', label: t('settings.tabs.notifications'), icon: Bell },
    { id: 'ai', label: t('settings.tabs.ai'), icon: Shield },
    { id: 'payRules', label: t('settings.tabs.payRules'), icon: DollarSign },
    { id: 'sector', label: t('settings.tabs.sector'), icon: Building2 },
    { id: 'roles', label: t('settings.tabs.roles'), icon: Users },
  ];

  const sectorPresetOptions = Object.keys(SECTOR_PRESETS);
  const currentSectorDefaults = SECTOR_PRESETS[sectorConfig.sector] || SECTOR_PRESETS.general;

  const formatSettingLabel = (key) => {
    if (key === 'defaultPayMultipliers') return 'Default Pay Multipliers';
    if (key === 'defaultSleepInRate') return 'Default Sleep-In Rate';
    if (key === 'defaultDisturbanceRate') return 'Default Disturbance Rate';
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase());
  };

  const formatMultiplierLabel = (key) => (
    key.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase())
  );

  const addCustomRole = () => {
    if (!newCustomRole.id || !newCustomRole.label) return;
    setRoleHierarchy(prev => ({
      ...prev,
      roles: { ...prev.roles, [newCustomRole.id]: { label: newCustomRole.label, level: newCustomRole.level, canSupervise: newCustomRole.canSupervise } },
      customRoles: [...(prev.customRoles || []), newCustomRole.id],
    }));
    setNewCustomRole({ id: '', label: '', level: 1, canSupervise: false });
  };

  const removeCustomRole = (roleId) => {
    setRoleHierarchy(prev => {
      const newRoles = { ...prev.roles };
      delete newRoles[roleId];
      const newSubs = { ...prev.substitutionRules };
      delete newSubs[roleId];
      return { ...prev, roles: newRoles, substitutionRules: newSubs, customRoles: (prev.customRoles || []).filter(r => r !== roleId) };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const admin = isAdmin(userData);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <SettingsIcon size={24} /> {t('settings.title')}
          </h1>
          <p className="text-gray-400 mt-1">{t('settings.subtitle')}</p>
        </div>
        {admin && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
          >
            {saved ? <Check size={18} /> : <Save size={18} />}
            {saved ? t('settings.saved') : saving ? t('settings.saving') : t('common.save')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/10 rounded-lg p-1 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white/10 text-primary'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      {/* General */}
      {activeTab === 'general' && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 space-y-6">
          <h3 className="font-semibold text-white">{t('settings.general.title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('settings.general.companyName')}</label>
              <input
                type="text"
                value={settings.companyName}
                disabled
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-gray-400 opacity-60"
              />
              <p className="text-xs text-gray-400 mt-1">{t('settings.general.companyNameHint')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                <Globe size={14} className="inline mr-1" /> {t('settings.general.timezone')}
              </label>
              <select
                value={settings.timezone}
                onChange={e => setSettings({ ...settings, timezone: e.target.value })}
                disabled={!admin}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white [&>option]:text-black [&>option]:bg-white"
              >
                <option value="Europe/London">Europe/London (GMT/BST)</option>
                <option value="Europe/Paris">Europe/Paris (CET)</option>
                <option value="Europe/Berlin">Europe/Berlin (CET)</option>
                <option value="America/New_York">America/New_York (EST)</option>
                <option value="America/Chicago">America/Chicago (CST)</option>
                <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
                <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                <option value="Asia/Shanghai">Asia/Shanghai (CST)</option>
                <option value="Australia/Sydney">Australia/Sydney (AEST)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('settings.general.weekStartsOn')}</label>
              <select
                value={settings.weekStartDay}
                onChange={e => setSettings({ ...settings, weekStartDay: parseInt(e.target.value) })}
                disabled={!admin}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white [&>option]:text-black [&>option]:bg-white"
              >
                <option value={0}>{t('settings.general.sunday')}</option>
                <option value={1}>{t('settings.general.monday')}</option>
                <option value={6}>{t('settings.general.saturday')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('settings.general.defaultShiftDuration')}</label>
              <input
                type="number"
                value={settings.defaultShiftDuration}
                onChange={e => setSettings({ ...settings, defaultShiftDuration: parseInt(e.target.value) })}
                disabled={!admin}
                min={1}
                max={24}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white"
              />
            </div>
          </div>
        </div>
      )}

      {/* Scheduling Rules */}
      {activeTab === 'scheduling' && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 space-y-6">
          <h3 className="font-semibold text-white">{t('settings.scheduling.title')}</h3>
          <p className="text-sm text-gray-400">{t('settings.scheduling.description')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('settings.scheduling.minRestHours')}</label>
              <input
                type="number"
                value={settings.minRestHours}
                onChange={e => setSettings({ ...settings, minRestHours: parseInt(e.target.value) })}
                disabled={!admin}
                min={0}
                max={24}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white"
              />
              <p className="text-xs text-gray-400 mt-1">{t('settings.scheduling.wtdDefault11')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('settings.scheduling.maxWeeklyHours')}</label>
              <input
                type="number"
                value={settings.maxWeeklyHours}
                onChange={e => setSettings({ ...settings, maxWeeklyHours: parseInt(e.target.value) })}
                disabled={!admin}
                min={1}
                max={168}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white"
              />
              <p className="text-xs text-gray-400 mt-1">{t('settings.scheduling.wtdDefault48')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('settings.scheduling.maxDailyHours')}</label>
              <input
                type="number"
                value={settings.maxDailyHours}
                onChange={e => setSettings({ ...settings, maxDailyHours: parseInt(e.target.value) })}
                disabled={!admin}
                min={1}
                max={24}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white"
              />
              <p className="text-xs text-gray-400 mt-1">{t('settings.scheduling.wtdDefault13')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('settings.scheduling.maxConsecutiveDays')}</label>
              <input
                type="number"
                value={settings.maxConsecutiveDays}
                onChange={e => setSettings({ ...settings, maxConsecutiveDays: parseInt(e.target.value) })}
                disabled={!admin}
                min={1}
                max={14}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white"
              />
              <p className="text-xs text-gray-400 mt-1">{t('settings.scheduling.defaultDays6')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      {activeTab === 'notifications' && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 space-y-6">
          <h3 className="font-semibold text-white">{t('settings.notifications.title')}</h3>
          <div className="space-y-4">
            {[
              { key: 'notifyOnPublish', label: t('settings.notifications.notifyOnPublish') },
              { key: 'notifyOnSwapRequest', label: t('settings.notifications.notifyOnSwap') },
              { key: 'notifyOnShiftChange', label: t('settings.notifications.notifyOnChange') },
            ].map(item => (
              <label key={item.key} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <span className="text-sm font-medium text-gray-300">{item.label}</span>
                <input
                  type="checkbox"
                  checked={settings[item.key]}
                  onChange={e => setSettings({ ...settings, [item.key]: e.target.checked })}
                  disabled={!admin}
                  className="w-5 h-5 rounded text-primary focus:ring-primary"
                />
              </label>
            ))}
            <div className="p-4 bg-white/5 rounded-lg">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('settings.notifications.reminderBefore')}
              </label>
              <select
                value={settings.notifyBeforeShift}
                onChange={e => setSettings({ ...settings, notifyBeforeShift: parseInt(e.target.value) })}
                disabled={!admin}
                className="bg-white/5 border border-white/20 rounded-lg px-4 py-2 text-white [&>option]:text-black [&>option]:bg-white"
              >
                <option value={0}>{t('settings.notifications.disabled')}</option>
                <option value={15}>{t('settings.notifications.min15')}</option>
                <option value={30}>{t('settings.notifications.min30')}</option>
                <option value={60}>{t('settings.notifications.hour1')}</option>
                <option value={120}>{t('settings.notifications.hours2')}</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* AI & Automation */}
      {activeTab === 'ai' && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 space-y-6">
          <h3 className="font-semibold text-white">{t('settings.ai.title')}</h3>
          <p className="text-sm text-gray-400">
            {t('settings.ai.description')}
          </p>
          <div className="space-y-4">
            {[
              { key: 'enableAISuggestions', label: t('settings.ai.enableSuggestions'), desc: t('settings.ai.enableSuggestionsDesc') },
              { key: 'enableAutoAssign', label: t('settings.ai.enableAutoAssign'), desc: t('settings.ai.enableAutoAssignDesc') },
              { key: 'showCoverageWarnings', label: t('settings.ai.showCoverage'), desc: t('settings.ai.showCoverageDesc') },
              { key: 'requireSwapApproval', label: t('settings.ai.requireSwapApproval'), desc: t('settings.ai.requireSwapApprovalDesc') },
              { key: 'autoApproveSwaps', label: t('settings.ai.autoApproveSwaps'), desc: t('settings.ai.autoApproveSwapsDesc') },
            ].map(item => (
              <label key={item.key} className="flex items-start justify-between p-4 bg-white/5 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-gray-300">{item.label}</span>
                  <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings[item.key]}
                  onChange={e => setSettings({ ...settings, [item.key]: e.target.checked })}
                  disabled={!admin}
                  className="w-5 h-5 rounded text-primary focus:ring-primary mt-0.5 ml-4 flex-shrink-0"
                />
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Pay Rules */}
      {activeTab === 'payRules' && (
        <div className="space-y-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 space-y-6">
            <h3 className="font-semibold text-white flex items-center gap-2"><DollarSign size={18} /> {t('payRulesSettings.title')}</h3>
            <p className="text-sm text-gray-400">{t('payRulesSettings.description')}</p>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('payRulesSettings.currency')}</label>
              <select
                value={payRules.currency || 'GBP'}
                onChange={e => setPayRules({ ...payRules, currency: e.target.value })}
                disabled={!admin}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white max-w-xs [&>option]:text-black [&>option]:bg-white"
              >
                {['GBP', 'USD', 'EUR', 'AUD', 'CAD', 'NZD', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'RON', 'INR', 'CNY', 'JPY', 'BRL'].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('payRulesSettings.sleepInDisplayMode')}</label>
              <select
                value={payRules.sleepInDisplayMode || 'hours'}
                onChange={e => setPayRules({ ...payRules, sleepInDisplayMode: e.target.value })}
                disabled={!admin}
                className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white max-w-md [&>option]:text-black [&>option]:bg-white"
              >
                <option value="hours">{t('payRulesSettings.sleepInAsHours')}</option>
                <option value="count">{t('payRulesSettings.sleepInAsCount')}</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">{t('payRulesSettings.sleepInDisplayHint')}</p>
            </div>

            <h4 className="text-sm font-medium text-white mt-4">{t('payRulesSettings.multipliers')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(payRules.multipliers || {}).map(([key, val]) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-400 mb-1 capitalize">{key.replace(/([A-Z])/g, ' $1')}</label>
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    value={val}
                    onChange={e => setPayRules({ ...payRules, multipliers: { ...payRules.multipliers, [key]: parseFloat(e.target.value) || 0 } })}
                    disabled={!admin}
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
              ))}
            </div>

            <h4 className="text-sm font-medium text-white mt-4">{t('payRulesSettings.flatRates')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(payRules.flatRates || {}).map(([key, val]) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-400 mb-1 capitalize">{key.replace(/([A-Z])/g, ' $1')}</label>
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400 text-sm">{payRules.currency || '£'}</span>
                    <input
                      type="number"
                      step="0.50"
                      min="0"
                      value={val}
                      onChange={e => setPayRules({ ...payRules, flatRates: { ...payRules.flatRates, [key]: parseFloat(e.target.value) || 0 } })}
                      disabled={!admin}
                      className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>

            <h4 className="text-sm font-medium text-white mt-4">{t('payRulesSettings.overtimeThresholds')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">{t('payRulesSettings.weeklyThreshold')}</label>
                <input
                  type="number"
                  min="0"
                  max="168"
                  value={payRules.overtimeThresholds?.weekly ?? 40}
                  onChange={e => setPayRules({ ...payRules, overtimeThresholds: { ...payRules.overtimeThresholds, weekly: parseInt(e.target.value) || 40 } })}
                  disabled={!admin}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">{t('payRulesSettings.dailyThreshold')}</label>
                <input
                  type="number"
                  min="0"
                  max="24"
                  value={payRules.overtimeThresholds?.daily ?? 8}
                  onChange={e => setPayRules({ ...payRules, overtimeThresholds: { ...payRules.overtimeThresholds, daily: parseInt(e.target.value) || 8 } })}
                  disabled={!admin}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">{t('payRulesSettings.overtimeMultiplier')}</label>
                <input
                  type="number"
                  step="0.05"
                  min="1"
                  value={payRules.overtimeThresholds?.overtimeMultiplier ?? 1.5}
                  onChange={e => setPayRules({ ...payRules, overtimeThresholds: { ...payRules.overtimeThresholds, overtimeMultiplier: parseFloat(e.target.value) || 1.5 } })}
                  disabled={!admin}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
            </div>
          </div>

          {/* Shift Templates */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <h3 className="font-semibold text-white mb-4">{t('settings.shiftTemplates')}</h3>
            <p className="text-sm text-gray-400 mb-4">{t('settings.shiftTemplatesDesc')}</p>
            <ShiftTemplateManager companyId={companyId} userId={user?.uid} userName={user?.displayName} />
          </div>
        </div>
      )}

      {/* Sector Configuration */}
      {activeTab === 'sector' && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 space-y-6">
          <h3 className="font-semibold text-white flex items-center gap-2"><Building2 size={18} /> {t('sectorSettings.title')}</h3>
          <p className="text-sm text-gray-400">{t('sectorSettings.description')}</p>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('sectorSettings.sector')}</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {sectorPresetOptions.map(s => (
                <button
                  key={s}
                  onClick={() => admin && setSectorConfig({ ...sectorConfig, sector: s, customOverrides: {} })}
                  className={`p-4 rounded-lg border text-left transition-colors ${
                    sectorConfig.sector === s
                      ? 'bg-primary/20 border-primary text-white'
                      : 'bg-white/5 border-white/10 text-gray-300 hover:border-white/30'
                  } ${!admin ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="font-medium capitalize text-sm">{s === 'general' ? `🏢 ${t('sectorSettings.general')}` : s === 'care' ? `🏥 ${t('sectorSettings.care')}` : s === 'hospitality' ? `🍽️ ${t('sectorSettings.hospitality')}` : s === 'security' ? `🔒 ${t('sectorSettings.security')}` : s === 'healthcare' ? `⚕️ ${t('sectorSettings.healthcare')}` : `🛒 ${t('sectorSettings.retail')}`}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white/5 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-medium text-white">{t('sectorSettings.defaults')}</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              {Object.entries(currentSectorDefaults).map(([key, val]) => (
                <div key={key} className="bg-white/5 rounded p-2">
                  <span className="text-gray-400 block">{formatSettingLabel(key)}</span>
                  {key === 'defaultPayMultipliers' && val && typeof val === 'object' ? (
                    <div className="mt-1 space-y-1">
                      {Object.entries(val).map(([multiplierKey, multiplierValue]) => (
                        <div key={multiplierKey} className="flex items-center justify-between gap-3 text-[11px]">
                          <span className="text-gray-300">{formatMultiplierLabel(multiplierKey)}</span>
                          <span className="text-white font-medium">{multiplierValue}x</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-white font-medium">
                      {typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-white mb-2">{t('sectorSettings.customOverrides')}</h4>
            <p className="text-xs text-gray-400 mb-3">{t('sectorSettings.customOverridesDesc')}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">{t('sectorSettings.maxShiftDuration')}</label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={sectorConfig.customOverrides?.maxShiftDuration ?? currentSectorDefaults.maxShiftDuration ?? 12}
                  onChange={e => setSectorConfig({ ...sectorConfig, customOverrides: { ...sectorConfig.customOverrides, maxShiftDuration: parseInt(e.target.value) } })}
                  disabled={!admin}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">{t('settings.scheduling.maxWeeklyHours')}</label>
                <input
                  type="number"
                  min="1"
                  max="168"
                  value={sectorConfig.customOverrides?.maxWeeklyHours ?? currentSectorDefaults.maxWeeklyHours ?? 48}
                  onChange={e => setSectorConfig({ ...sectorConfig, customOverrides: { ...sectorConfig.customOverrides, maxWeeklyHours: parseInt(e.target.value) } })}
                  disabled={!admin}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">{t('sectorSettings.maxConsecutiveNights')}</label>
                <input
                  type="number"
                  min="1"
                  max="14"
                  value={sectorConfig.customOverrides?.maxConsecutiveNights ?? currentSectorDefaults.maxConsecutiveNights ?? 7}
                  onChange={e => setSectorConfig({ ...sectorConfig, customOverrides: { ...sectorConfig.customOverrides, maxConsecutiveNights: parseInt(e.target.value) } })}
                  disabled={!admin}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-gray-300">
                  <input
                    type="checkbox"
                    checked={sectorConfig.customOverrides?.allowSleepIns ?? currentSectorDefaults.allowSleepIns ?? false}
                    onChange={e => setSectorConfig({ ...sectorConfig, customOverrides: { ...sectorConfig.customOverrides, allowSleepIns: e.target.checked } })}
                    disabled={!admin}
                    className="w-4 h-4 rounded text-primary"
                  />
                  {t('sectorSettings.allowSleepIns')}
                </label>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-gray-300">
                  <input
                    type="checkbox"
                    checked={sectorConfig.customOverrides?.allowSplitShifts ?? currentSectorDefaults.allowSplitShifts ?? false}
                    onChange={e => setSectorConfig({ ...sectorConfig, customOverrides: { ...sectorConfig.customOverrides, allowSplitShifts: e.target.checked } })}
                    disabled={!admin}
                    className="w-4 h-4 rounded text-primary"
                  />
                  {t('sectorSettings.allowSplitShifts')}
                </label>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-gray-300">
                  <input
                    type="checkbox"
                    checked={sectorConfig.customOverrides?.requireSupervisor ?? currentSectorDefaults.requireSupervisor ?? false}
                    onChange={e => setSectorConfig({ ...sectorConfig, customOverrides: { ...sectorConfig.customOverrides, requireSupervisor: e.target.checked } })}
                    disabled={!admin}
                    className="w-4 h-4 rounded text-primary"
                  />
                  {t('sectorSettings.requireSupervisor')}
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Hierarchy */}
      {activeTab === 'roles' && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 space-y-6">
          <h3 className="font-semibold text-white flex items-center gap-2"><Users size={18} /> {t('roleHierarchy.title')}</h3>
          <p className="text-sm text-gray-400">{t('roleHierarchy.description')}</p>

          <div className="space-y-2">
            <h4 className="text-sm font-medium text-white">{t('roleHierarchy.currentRoles')}</h4>
            <div className="space-y-1">
              {Object.entries(roleHierarchy.roles || {})
                .sort(([, a], [, b]) => (b.level || 0) - (a.level || 0))
                .map(([roleId, role]) => {
                  const isCustom = (roleHierarchy.customRoles || []).includes(roleId);
                  return (
                    <div key={roleId} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`inline-block w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          role.level >= 4 ? 'bg-purple-500/20 text-purple-300' :
                          role.level >= 3 ? 'bg-blue-500/20 text-blue-300' :
                          role.level >= 2 ? 'bg-green-500/20 text-green-300' :
                          'bg-gray-500/20 text-gray-300'
                        }`}>{role.level}</span>
                        <div>
                          <span className="text-sm font-medium text-white">{role.label}</span>
                          <span className="text-xs text-gray-400 ml-2">({roleId})</span>
                          {role.canSupervise && <span className="text-xs text-yellow-400 ml-2">★ {t('roleHierarchy.supervisor')}</span>}
                          {isCustom && <span className="text-xs text-primary ml-2">{t('roleHierarchy.custom')}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isCustom && admin && (
                          <button onClick={() => removeCustomRole(roleId)} className="text-red-400 hover:text-red-300 p-1">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {admin && (
            <div className="bg-white/5 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-medium text-white flex items-center gap-2"><Plus size={14} /> {t('roleHierarchy.addRole')}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('roleHierarchy.roleId')}</label>
                  <input
                    type="text"
                    placeholder="e.g. team_lead"
                    value={newCustomRole.id}
                    onChange={e => setNewCustomRole({ ...newCustomRole, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('roleHierarchy.displayName')}</label>
                  <input
                    type="text"
                    placeholder="e.g. Team Lead"
                    value={newCustomRole.label}
                    onChange={e => setNewCustomRole({ ...newCustomRole, label: e.target.value })}
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">{t('roleHierarchy.seniorityLevel')}</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    value={newCustomRole.level}
                    onChange={e => setNewCustomRole({ ...newCustomRole, level: parseInt(e.target.value) || 0 })}
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
                <div className="flex flex-col justify-end gap-2">
                  <label className="flex items-center gap-2 text-xs text-gray-300">
                    <input
                      type="checkbox"
                      checked={newCustomRole.canSupervise}
                      onChange={e => setNewCustomRole({ ...newCustomRole, canSupervise: e.target.checked })}
                      className="w-4 h-4 rounded text-primary"
                    />
                    {t('roleHierarchy.canSupervise')}
                  </label>
                  <button
                    onClick={addCustomRole}
                    disabled={!newCustomRole.id || !newCustomRole.label}
                    className="bg-primary text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-dark disabled:opacity-50"
                  >
                    {t('roleHierarchy.addRole')}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
            <p className="text-xs text-amber-300">💡 {t('roleHierarchy.hint')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
